/**
 * Adapte les réponses API analytics (dashboard / overview) au state UI.
 */

import { resolveProductLineCa } from './productAnalytics';
import { mapCanauxMetierUiLabels } from './canalMetierLabels';

const mapTopProduitsDashboard = (items = []) =>
  items.map((item) => ({
    nom: item.produit?.nom || 'Produit inconnu',
    ventes: item.quantite ?? item._sum?.quantite ?? 0,
    revenus: resolveProductLineCa(item),
    partCaPct: item.partCaPct ?? 0,
    boutique: item.produit?.commercant?.nom || null,
  }));

export function mapDashboardAnalytics(data) {
  const totalRevenus = data.totalRevenus || 0;
  /** Volume financier canonique (VALIDE|REMBOURSE hors Gift ISSUE) — dénominateur panier. */
  const volumeVentes =
    data.volumeVentes != null ? Number(data.volumeVentes) : null;
  const totalCommandesLegacy = data.totalCommandes || 0;
  const paidOrdersCount =
    volumeVentes != null && Number.isFinite(volumeVentes)
      ? volumeVentes
      : totalCommandesLegacy;
  const panierMoyen =
    data.panierMoyen ??
    (paidOrdersCount > 0 ? totalRevenus / paidOrdersCount : 0);

  const revenusParJour = (data.evolutionCommandes || []).map((item) => {
    const nombreCommandes = item._count?.id || 0;
    const ca = item.caNet ?? item.ca ?? nombreCommandes * panierMoyen;
    return {
      date: item.createdAt,
      revenus: ca,
      montant: ca,
      value: ca,
      commandes: nombreCommandes,
    };
  });

  const revenusParMoisMap = new Map();
  revenusParJour.forEach((item) => {
    if (!item.date) return;
    const date = new Date(item.date);
    const moisKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const moisLabel = date.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
    if (!revenusParMoisMap.has(moisKey)) {
      revenusParMoisMap.set(moisKey, { mois: moisLabel, revenus: 0, commandes: 0 });
    }
    const moisData = revenusParMoisMap.get(moisKey);
    moisData.revenus += item.revenus;
    moisData.commandes += item.commandes;
  });

  return {
    revenus: {
      total: totalRevenus,
      brut: data.caBrut ?? totalRevenus,
      rembourse: data.caRembourse ?? 0,
      evolution: data.caDeltaPct ?? data.revenusEvolution ?? null,
      parJour: revenusParJour,
      parMois: Array.from(revenusParMoisMap.values()),
      panierMoyen,
      label: data.revenusLabel || 'CA net ventes',
    },
    commandes: {
      total: paidOrdersCount,
      volumeVentes: paidOrdersCount,
      label: 'Commandes payées',
      evolution: data.commandesDeltaPct ?? data.commandesEvolution ?? null,
      parStatut: (data.commandesParStatut || []).map((item) => ({
        statut: item.statut,
        count: item._count?.statut || 0,
      })),
      parCanal: mapCanauxMetierUiLabels(data.commandesParCanal || []),
      parPaiement: data.commandesParPaiement || [],
      parJour: (data.evolutionCommandes || []).map((item) => ({
        date: item.createdAt,
        count: item._count?.id || 0,
      })),
    },
    utilisateurs: {
      total: data.totalUsers != null ? Number(data.totalUsers) : null,
      evolution: null,
      nouveaux: null,
    },
    commercants: {
      total: data.totalCommercants != null ? Number(data.totalCommercants) : null,
      actifs: data.commercantsActifs ?? null,
      topPerformers: [],
    },
    produits: {
      total: (data.totalProduits || 0) + (data.totalPrestations || 0),
      topVendus: mapTopProduitsDashboard(data.topProduits),
    },
    periodLabel: data.periodLabel,
  };
}

export function mapOverviewAnalytics(overview) {
  const kpi = overview.kpi || {};
  const totalRevenus = kpi.ca?.value ?? 0;
  const totalCommandes = kpi.commandes?.value ?? 0;
  const panierMoyen = kpi.panierMoyen?.value ?? 0;

  return {
    revenus: {
      total: totalRevenus,
      brut: kpi.ca?.brut ?? totalRevenus,
      rembourse: kpi.ca?.rembourse ?? 0,
      evolution: kpi.ca?.deltaPct ?? null,
      parJour: (overview.evolution || []).map((row) => ({
        date: row.date,
        revenus: row.ca,
        montant: row.ca,
        value: row.ca,
        commandes: row.commandes,
      })),
      // Fallback courbe dashboard : série journalière (pas de moteur mensuel dédié en overview)
      parMois: (overview.evolution || []).map((row) => ({
        mois: row.date,
        revenus: row.ca,
        commandes: row.commandes,
      })),
      panierMoyen,
      label: kpi.ca?.label || 'CA net ventes',
    },
    commandes: {
      total: totalCommandes,
      evolution: kpi.commandes?.deltaPct ?? null,
      parStatut: (overview.commandesParStatut || []).map((item) => ({
        statut: item.statut,
        count: item.count,
      })),
      parCanal: mapCanauxMetierUiLabels(overview.canaux || []),
      parPaiement: overview.paiements || [],
      parJour: (overview.evolution || []).map((row) => ({
        date: row.date,
        count: row.commandes,
      })),
    },
    utilisateurs: {
      total: overview.totalUsers != null ? Number(overview.totalUsers) : null,
      evolution: null,
      nouveaux: null,
    },
    commercants: {
      total: overview.totalCommercants != null ? Number(overview.totalCommercants) : null,
      actifs: overview.commercantsActifs ?? null,
      topPerformers: [],
    },
    produits: {
      total: 0,
      topVendus: (overview.topProduitsCa || []).map((p) => ({
        nom: p.nom,
        ventes: p.quantite,
        revenus: p.ca,
        partCaPct: p.partCaPct,
        boutique: null,
      })),
    },
    periodLabel: overview.periode?.debutLocal
      ? `${overview.periode.debutLocal} → ${overview.periode.finLocal}`
      : null,
  };
}

export const CHART_COLORS = [
  'rgb(59, 130, 246)',
  'rgb(34, 197, 94)',
  'rgb(251, 191, 36)',
  'rgb(168, 85, 247)',
  'rgb(236, 72, 153)',
  'rgb(107, 114, 128)',
];
